import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function WhyChooseUs() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [animated, setAnimated] = useState(false);

  const stats = [
    { label: t('whyChooseUs.stats.satisfaction.label'), value: 100 },
    { label: t('whyChooseUs.stats.onTime.label'), value: 98 },
    { label: t('whyChooseUs.stats.reliability.label'), value: 99 },
    { label: t('whyChooseUs.stats.quality.label'), value: 97 },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAnimated(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section className="overflow-hidden" ref={sectionRef}>
      <div className="flex flex-col lg:flex-row min-h-[520px]">
        {/* Image side */}
        <div className="relative w-full lg:w-1/2 min-h-[340px] lg:min-h-[520px] bg-black flex items-center justify-center cursor-pointer group rounded-2xl overflow-hidden">
          <img
            alt="Completed screen door project by The Screen Squad"
            className="w-full h-full object-cover object-center"
            src="https://storage.helloreaddy.io/project_files/95272ccd-89f8-41e5-a90f-f2c2b48cc48a/b22c1235-560f-4596-99f8-956f49d44a51_compressed_magnific_reframe-this-exact-photog_l7rZLUbgv9.webp"
          />
          <div className="absolute bottom-4 left-0 right-0 flex justify-center z-10">
            <span className="bg-black/60 text-white text-xs font-semibold px-4 py-1.5 rounded-full tracking-wide">
              {t('whyChooseUs.label')}
            </span>
          </div>
        </div>

        {/* Content side */}
        <div className="w-full lg:w-1/2 bg-background-50 flex flex-col justify-center px-8 md:px-16 py-14">
          <span className="inline-block text-xs font-bold uppercase tracking-[0.25em] text-primary-500 border border-primary-500/40 rounded-full px-4 py-1 mb-5 w-fit">
            {t('whyChooseUs.badge')}
          </span>
          <h2 className="text-2xl md:text-3xl font-extrabold text-foreground-50 leading-snug mb-4">
            {t('whyChooseUs.title')}
          </h2>
          <p className="text-foreground-400 text-sm leading-relaxed mb-8 max-w-md">
            {t('whyChooseUs.description')}
          </p>

          <div className="space-y-5">
            {stats.map((stat, i) => (
              <div key={i}>
                <div className="flex justify-between items-center mb-1.5">
                  <span className="text-xs font-bold uppercase tracking-[0.25em] text-foreground-400">
                    {stat.label}
                  </span>
                  <span className="text-sm font-extrabold text-accent-500">{stat.value}%</span>
                </div>
                <div className="w-full bg-foreground-500/10 rounded-full h-2">
                  <div
                    className="h-2 rounded-full bg-gradient-to-r from-primary-900 to-primary-500 transition-all duration-1000 ease-out"
                    style={{ width: animated ? `${stat.value}%` : '0%' }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}